import Vue from 'vue';
import dayjs from 'dayjs';

import { router } from '@/router';
import { setDayjsLocale } from '@/assets/dayjsLocales';
import { i18n } from '@/tools/i18n';
import { meaning, fontSizes, colorModes } from '@/tools/consts';
import { logger } from '@/tools/logger';
import { createObjectFromArray, createObjectFromArrayByKey } from '@/tools/createObjectFromArray';
import { sleep } from '@/tools/sleep';
import { uiComponents, uiStates } from '@/tools/viewState';
import { mutationTypes, actionTypes, errorTypes, articleTypes, exhibitionTypes, getterTypes } from '@/types';
import { renderHeaderAndText, renderExternal } from '@/tools/rendering';
import { setUrlToDataSource } from '@/tools/setUrlToDataSource';
import { registerModule } from './registerModule';
import { nativeMethodCall, initializeNativeInterface } from '@/tools/nativeInterface';
import packageJson from '../../package.json';

const PRESERVED_URL_VARS = [
	'mode',
	'ds',
	'css',
	'app',
	'showLogMessages',
	'isPreviewDataSource',
	'isVirtualPackageSyncer',
	'pageTransition',
	'plugin',
	'pluginName',
];

function getArticleVersion({ state, articleID, exhibitionID = state.currentExhibitionID }) {
	const exhibition = state.dataSourceCFG && state.dataSourceCFG.exhibitionMap[exhibitionID];

	if (!exhibition) return;
	const articles = exhibition.articles.find(article => article[0] === articleID);

	return articles && articles[1].version;
}

function toLeafLetGeoForms(geoform) {
	geoform.coords = geoform.coords.map(coords => {
		return {
			lat: coords[1],
			lng: coords[0],
		};
	});
	return geoform;
}

// create style node for styleTemplate for the content this href attribute will be set later
const $css = document.createElement('style');

const articleIsLoading = new Set();

function isMapAvailable(id, getters, state, dispatch) {
	if (id !== 'ui--map') return true;
	if (
		// 1) Exhibition online map
		getters.currentExhibition.hasOnlineNavigationMap ||
		// 2) Exhibition custom map
		getters.currentExhibition.customMap ||
		// 3) DataSource online map
		state.dataSourceCFG.global.hasOnlineNavigationMap ||
		// 4) DataSource custom map
		state.dataSourceCFG.global.customMap
	) {
		return true;
	}
	dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.mapNotConfigured) });
}

function isValidModalId(id, dispatch) {
	if (!Object.values(uiComponents).includes(id)) {
		dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.viewStateUnkownUiComponent) });
		return false;
	}
	return true;
}

export default {
	[actionTypes.sendErrorMessage]({ commit }, { error, options }) {
		const _options = Object.assign(
			{
				sticky: false,
				title: null,
				type: meaning.error,
				timeSpan: 5000,
				templateData: null,
			},
			options
		);
		let body = i18n.t('error.' + error.message);
		if (_options.templateData) {
			body = body.replace(/%(\w*)%/g, (match, key) => _options.templateData[key] || '');
		}
		commit(mutationTypes.sendMessage, {
			type: _options.type,
			body,
			title: _options.title,
			sticky: _options.sticky,
			timeSpan: _options.timeSpan,
		});
		logger.warn(error);
	},
	async [actionTypes.loadStoreModule]({ commit }, { moduleName }) {
		if (moduleName === 'playerAudio') {
			const loadedModule = await import(
				/* webpackChunkName: "store/playerAudio" */ './modules/playerAudio/index'
			);
			registerModule(moduleName, loadedModule);
			commit(mutationTypes.storeModuleLoaded, { moduleName });
		}
	},
	[actionTypes.setShowLogMessages](_, toggleState) {
		if (!toggleState) {
			window.console.log('############ Log messages are turned OFF ############');
			Object.keys(logger)
				.filter(key => key !== 'error')
				.forEach(key => {
					logger[key] = () => {};
				});
		} else {
			/* eslint-disable no-console */
			logger.log = console.log;
			logger.warn = console.warn;
			logger.groupCollapsed = console.groupCollapsed;
			logger.group = console.group;
			logger.groupEnd = console.groupEnd;
			window.console.log('############ Log messages are turned ON ############');
			/* eslint-enable no-console */
		}
	},
	async [actionTypes.pushUrl]({ state, commit }, { url, replace }) {
		commit(mutationTypes.setRouteChangedByClick, { routeUserClicked: true });
		// Preserve url vars.
		const parts = url.split('?');
		const urlVars = parts[1] ? parts[1].split('&').map(s => s.split('=')) : [];
		const urlVarsKeys = urlVars.map(v => v[0]);
		const perservedEntries = Object.entries(state.route.query).filter(
			entry => PRESERVED_URL_VARS.includes(entry[0]) && !urlVarsKeys.includes(entry[0])
		);
		const urlWithVars = `${parts[0]}?${urlVars
			.concat(perservedEntries)
			.map(e => (e[1] ? e.join('=') : e[0]))
			.join('&')}`;
		try {
			if (replace) await router.replace(urlWithVars);
			// If the router redirects the promise is rejected see https://github.com/vuejs/vue-router/issues/2932
			// we do not want to have uncaught promise rejections in this case, we just ignore it.
			else await router.push({ path: urlWithVars }).catch(() => {});
		} catch (error) {
			// vue-router throws an error if you try to replace with the same url
			if (!error || !error.name || error.name !== 'NavigationDuplicated') {
				throw error;
			}
		}
	},
	/**
	 * This Action loads content via async ajax call or renders content depending on `exhibition.type`
	 * and `article.type`
	 */
	async [actionTypes.loadContent]({ state, getters, commit, dispatch }, { articleID, exhibitionID, tourID }) {
		// const articleID = state.currentArticleID;
		const language = getters.currentLanguage;

		const exhibition = state.dataSourceCFG.exhibitionMap[exhibitionID];
		const article = exhibition.articleMap[articleID];
		if (!state.dataSourceURL) {
			logger.error('actionTypes.loadContent: DataSource was not loaded yet.');
			return;
		}

		// Handling of all root pages like exhibitions, tours, all articles
		if (exhibition.type === exhibitionTypes.root) {
			if (articleID === 'exhibition-list') {
				dispatch(actionTypes.searchShowExhibitions, { options: { closeButton: false } });
				return;
			}
			// show all Tours of datasource
			if (articleID === 'tour-list') {
				dispatch(actionTypes.searchShowTours, { options: { closeButton: false } });
				return;
			}
			if (articleID === 'article-list') {
				dispatch(actionTypes.searchShowAllArticles, { options: { closeButton: false, sortBy: 'title' } });
				return;
			}
		}
		// Show all tours filtered by exhibition
		if (articleID === 'tour-list') {
			dispatch(actionTypes.searchShowTours, {
				options: { exhibitionID: state.currentExhibitionID, closeButton: false },
			});
			return;
		}
		if (article.type === articleTypes.tourList) {
			dispatch(actionTypes.searchShowTour, {
				exhibitionID: state.currentExhibitionID,
				tourID,
				options: { closeButton: false, sortBy: 'name' },
			});
			return;
		}
		if (article.type === articleTypes.articleList) {
			// Open search with depending on exhibition setting right focus to list all articles
			if (exhibition.type === exhibitionTypes.glossary) {
				dispatch(actionTypes.searchShowGlossary, { options: { closeButton: false } });
			} else if (exhibition.type === exhibitionTypes.calendar) {
				dispatch(actionTypes.searchShowCalendar, { options: { closeButton: false } });
			} else {
				dispatch(actionTypes.searchShowArticles, {
					exhibitionID: state.currentExhibitionID,
					options: { closeButton: false, sortBy: 'title' },
				});
			}
			return;
		}
		if (article.type === articleTypes.ar) {
			return '<div class="arDummyContent" style="background-color:#000000; width: 100%; height:100vh"></div>';
		}
		if (article.type === articleTypes.rawContent) {
			// Handle articles like glossaryTerm and calendarEntry.
			let languageContent = article.languageContent.filter(
				({ languageISO }) => languageISO === state.persistent.language
			);
			if (!languageContent) {
				languageContent = article.languageContent.filter(
					({ languageISO }) => languageISO === state.defaultLanguage
				);
			}
			if (!languageContent) languageContent = article.languageContent[0];

			const isGlossary = exhibition.type === exhibitionTypes.glossary;

			const content = renderHeaderAndText({
				imageSize: state.imageSize,
				hasHeader: !isGlossary,
				mediaObjectCover: article.mediaFileID
					? {
							id: article.mediaFileID,
							sourceUrl: setUrlToDataSource(`../media-${article.mediaFileID}`, state.dataSourceUrl),
					  }
					: undefined,
				pageTheme: article.pageTheme,
				content: isGlossary ? languageContent[0].content : languageContent.content,
				title: languageContent.title,
				description: languageContent.description,
				date: article.date,
				back: isGlossary
					? {
							command: 'command://setExhibitionLanding',
							label: getters.currentLanguage === 'de' ? 'Zurück' : 'Back',
					  }
					: null,
			});
			commit(mutationTypes.setContentForArticle, {
				id: articleID,
				content,
				language,
			});
			return content;
		}
		if (article.type === articleTypes.unityScene) {
			const { id, version } = article;
			commit(mutationTypes.setContentForArticle, {
				id,
				content: renderExternal({
					url: setUrlToDataSource(`../article-${id}-${version}/index.html`),
				}),
				language,
			});
			return;
		}
		if (article.type === articleTypes.external) {
			const { id, url } = article;
			const content = renderExternal({
				url,
			});
			commit(mutationTypes.setContentForArticle, {
				id,
				content,
				language,
			});
			return content;
		}
		if (!(state.articleContent[articleID] && state.articleContent[articleID][language])) {
			await dispatch(actionTypes.fetchArticleContent, { articleID, exhibitionID });
		}
		// Check if the fetchArticleContent was a success
		if (state.articleContent[articleID] && state.articleContent[articleID][language]) {
			return state.articleContent[articleID][language];
		} else {
			logger.warn('The requested content was not available.');
		}
	},
	/**
	 *
	 * @param {*} store
	 * @param { articleID, exhibitionID, modal } options modal indicates the content is loading for Content Modal
	 */
	async [actionTypes.fetchArticleContent]({ commit, state, getters, dispatch }, { articleID, exhibitionID }) {
		// handle all other articles pre rendered by cms
		const language = getters.currentLanguage;
		const articleVersion = getArticleVersion({ state, articleID, exhibitionID });
		logger.log(`=== fetchArticleContent ${articleID} and ex:${exhibitionID}  version: ${articleVersion}`);

		// Test for virtualPackageSyncer if Package is downloaded it only works not in preview mode.
		// Beacause of package indexing for that this check will not be applied
		if (state.isVirtualPackageSyncer && state.packageSyncer.isSyncerApp) {
			if (
				!state.isPreviewDataSource &&
				!state.packageSyncer.offlineAvailablePackagesIndex.has(`article-${articleID}-${articleVersion}`)
			) {
				logger.log(
					'Virtual Package Syncer could not find ' +
						`article-${articleID}-${articleVersion} in exhibition ${exhibitionID}`
				);
				await sleep(1000);
				dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.articleDownloadFailed) });
				return;
			}
		}
		const url = new URL(
			`./apps/article-${articleID}-${articleVersion}/${language}.content`,
			state.dataSourceURL
		);
		let result;
		try {
			result = await Vue.http.get(url.toString());
		} catch (error) {
			dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.articleDownloadFailed) });
			articleIsLoading.delete(articleID);
			return;
		}
		commit(mutationTypes.setContentForArticle, { id: articleID, content: result.body, language });
		// Set Timeout to leave eventloop so content will be rendered before check for the dom elements
	},
	// Loads CSS file if `cssDevUrl` is set as URL-variable it is loaded if not CSS URL is calculated.
	async [actionTypes.setStyleTemplate]({ state, commit, dispatch }, { exhibitionID }) {
		const cssDevUrl = state.route.query.css;
		if (cssDevUrl) {
			dispatch(actionTypes.setStyleUrl, { styleTemplateURL: cssDevUrl });
			commit(mutationTypes.activeCssMode);
			return;
		}
		let styleTempateHashQuery = '';
		let styleTemplateHashes = [];
		const isExhbitionStyle = exhibitionID && state.dataSourceCFG.exhibitionMap[exhibitionID].styleTemplateID;
		const styleTemplateBaseObject = isExhbitionStyle
			? state.dataSourceCFG.exhibitionMap[exhibitionID]
			: state.dataSourceCFG.global;
		if (!state.dataSourceCFG.global.isProgressiveWebApp && styleTemplateBaseObject.styleTemplateHashes) {
			({ styleTemplateHashes } = styleTemplateBaseObject);
			const [, hash] = styleTemplateHashes.find(([name]) => name === 'main.css');
			styleTempateHashQuery = `?v=${hash}`;
		}
		const { styleTemplateID } = styleTemplateBaseObject;
		const url = new URL(
			`./apps/style-${styleTemplateID}/main.css${styleTempateHashQuery}`,
			state.dataSourceURL
		);
		dispatch(actionTypes.setStyleUrl, { styleTemplateURL: url.toString(), styleTemplateHashes });
	},
	async [actionTypes.setStyleUrl](
		{ state, commit, dispatch },
		{ styleTemplateURL, styleTemplateHashes = [] }
	) {
		if (styleTemplateURL === state.styleTemplateURL) return;
		commit(mutationTypes.setStyleUrl, { styleTemplateURL });
		commit(mutationTypes.setStyleLoaded, { status: false });
		let result;
		try {
			result = await Vue.http.get(styleTemplateURL, { credentials: false });
		} catch (error) {
			dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.failedLoadingStyles) });
			return;
		}
		// Since the `url()` values use relative paths (if not `url('data:..')` is used)
		// we need to make them absolute relative the the location of the loaded style sheet.
		const urlParts = styleTemplateURL.split('/');
		urlParts.pop();
		// Replace all files linked in the styletemplate with cache breaking URLs
		$css.innerHTML = styleTemplateHashes.reduce(
			(acc, [name, hash]) => acc.replace(name, `${name}?v=${hash}`),
			result.body.replace(/url\(([^'"])/g, `url(${urlParts.join('/')}/$1`)
		);
		if (!$css.parentNode) document.head.appendChild($css);

		// Check the version of the CSS stylsheet and compare
		// the major version with the on of this webclient.
		// If they missmatch the API could be different. If this
		// is the case show a warning.
		commit(mutationTypes.setStyleLoaded, { status: true });
		const cssVersion = window
			.getComputedStyle(document.querySelector('#VERSION'), ':after')
			.getPropertyValue('content');
		if (cssVersion !== 'none') {
			window.console.log(`%c CSS ${cssVersion}`, 'text-align: center; font-size: 25px;');
			const [, cssVersionString] = cssVersion.split(' ');
			const [cssMajorVersion] = cssVersionString.split('.');
			const [webclientMajorVersion] = packageJson.version.split('.');
			if (cssMajorVersion !== webclientMajorVersion) {
				dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.cssVersionMissmatch) });
			}
		} else if (!state.cssDevMode) {
			dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.cssVersionMissing) });
		}
	},
	async [actionTypes.analyticsInitialize]({ state }) {
		if (state.isPreview) return; // do not track the preview frame in the CMS
		window._paq = [];
		const g = document.createElement('script');
		const s = document.getElementsByTagName('script')[0];
		const host =
			state.isDevServer || window.location.host.indexOf('staging') !== -1
				? 'athena.staging.shoutrlabs.com'
				: 'athena.cms.shoutrlabs.com';
		const u = `https://${host}/`;
		/* tracker methods like "setCustomDimension" should be called before "trackPageView" */
		// Set configuration here
		window._paq.push(['setTrackerUrl', `${u}etna/`]);
		window._paq.push(['enableLinkTracking']);

		g.type = 'text/javascript';
		g.async = true;
		g.defer = true;
		g.src = `${u}athena.js`;
		s.parentNode.insertBefore(g, s);
	},
	[actionTypes.analyticsLog]({ state }, { exhibitionID, articleID }) {
		if (state.isPreview) return; // do not track the preview frame in the CMS
		const exhibition = state.dataSourceCFG.exhibitionMap[exhibitionID];
		if (window._paq) {
			// Set a new site id for the Matomo analytics if Matomo is loaded and a site id is available.
			const { analyticsForOrganisation, analyticsSiteId } = state.dataSourceCFG.global;
			const siteId = analyticsForOrganisation ? analyticsSiteId : exhibition.analyticsSiteId;
			if (!siteId) {
				logger.warn('Matomo site id not found. Can not track user activity.');
				return;
			}
			window._paq.push(['setSiteId', siteId]);
			const _name = exhibition.articleMap[articleID].name || articleID;
			window._paq.push(['setDocumentTitle', _name]);
			window._paq.push(['trackPageView']);
		}
	},
	async [actionTypes.updateStyleContent]({ state, commit }) {
		logger.log('Action update StyleContent: dispatched');
		const result = await Vue.http.get(state.styleTemplateURL + `#${Date.now()}`);
		commit(mutationTypes.activeCssMode, {
			content: result.body,
		});
	},
	async [actionTypes.loadDataSource](
		{ commit, dispatch, state, getters },
		{ dataSourceURL, exhibitionID, articleID, tourID, plugin }
	) {
		logger.log('Action: loadDataSource dispatch with ', {
			dataSourceURL,
			exhibitionID,
			tourID,
			articleID,
			plugin,
		});
		// Download the `datasource.cfg` file.
		let dataSourceCFG;
		const dsURL = `${dataSourceURL}${/\/$/.test(dataSourceURL) ? '' : '/'}`;
		const url = new URL(`./cache/datasource.cfg#${Date.now()}`, dsURL);

		try {
			dataSourceCFG = (await Vue.http.get(url.toString())).body;
		} catch (error) {
			dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.dataSourceConfigDownloadFailed) });
			return;
		}

		// Start observing connection state to cms and boxx;
		// dispatch(actionTypes.startObservingConnectionState); // FIXME this feature was not ready yet and was therefore removed for now

		// Create lookup objects that allow you to retrieve `beacon`s, `exhibition`s, `location`s
		// by their id. In the initial download of the data source the are transmitted as arrays.
		dataSourceCFG.beaconDescMap = createObjectFromArray(dataSourceCFG.beaconDescription);
		dataSourceCFG.exhibitionMap = createObjectFromArrayByKey(dataSourceCFG.exhibitions, 'exhibitionID');
		const locationMap = (dataSourceCFG.locations || []).reduce(
			(acc, location) =>
				Object.assign(acc, {
					[location._id]: Object.assign(location, { articles: [], tours: [], exhibitions: new Set() }),
				}),
			{}
		);

		// Initialize Tours
		// Tours legacy fallback: if tours does not exist because
		// of legacy datasource create empty tours object
		if (!dataSourceCFG.tours) dataSourceCFG.tours = [];
		commit(mutationTypes.tourInit, {
			tours: dataSourceCFG.tours,
			exhibitionMap: dataSourceCFG.exhibitionMap,
		});

		// Initialize Tutorial
		commit(mutationTypes.tutorialInit, {
			exhibitions: dataSourceCFG.exhibitions,
			toursMap: state.tour.toursMap,
		});

		// Add the tours to each location so the search
		// under the map can show tours for locations
		// see `modules/search/actions/searchShowLocationsByList`
		dataSourceCFG.tours.forEach(tour => {
			if (tour.startLocationID) locationMap[tour.startLocationID].tours.push(tour);
			if (tour.pathLocationID) locationMap[tour.pathLocationID].tours.push(tour);
		});
		// Create virtual pages in exhibitions
		dataSourceCFG.exhibitions.forEach(exhibition => {
			// Add to every exhibition the overview article Meta Information.
			exhibition.languageContent = exhibition.languageContent.map(lc => lc[1]); // make languageContent of Exhibitions same as articles
			exhibition.languages = exhibition.languageContent.map(lC => lC.languageISO);
			exhibition.articles.push([
				articleTypes.articleList,
				{
					pageTheme: 'article-list',
					id: articleTypes.articleList,
					name: articleTypes.articleList,
					type: articleTypes.articleList,
					languageContent: exhibition.languageContent,
					triggers: [],
				},
			]);
			exhibition.articles.push([
				'tour-list',
				{
					pageTheme: 'tour-list',
					id: 'tour-list',
					name: 'tour-list',
					type: articleTypes.articleList,
					languageContent: exhibition.languageContent,
					triggers: [],
				},
			]);

			exhibition.articleMap = createObjectFromArray(exhibition.articles);

			if (typeof exhibition.geopoints === 'undefined') {
				// Initialize geopoints if not already set.
				exhibition.geopoints = [];
			}
			if (typeof exhibition.codes === 'undefined') {
				exhibition.codes = [];
			}
			if (typeof exhibition.beacons === 'undefined') {
				exhibition.beacons = [];
			}
			exhibition.geopoints.forEach(([, article]) => {
				article.exhibitionID = exhibition.exhibitionID;
			});
			exhibition.geopoints.forEach(keyVal => {
				toLeafLetGeoForms(keyVal[1]);
			});

			exhibition.codes.forEach(([, article]) => {
				article.exhibitionID = exhibition.exhibitionID;
			});
			exhibition.beacons.forEach(([, article]) => {
				article.exhibitionID = exhibition.exhibitionID;
			});
			exhibition.geopointsMap = createObjectFromArray(exhibition.geopoints);
			exhibition.codeMap = createObjectFromArray(exhibition.codes);
			exhibition.beaconMap = createObjectFromArray(exhibition.beacons);

			// Add the landing article to the locations
			(exhibition.triggers || []).forEach(({ locationID }) => {
				locationMap[locationID].exhibitions.add(exhibition.exhibitionID);
				locationMap[locationID].articles.push({
					article: exhibition.articleMap[exhibition.landingArticleID],
					exhibitionID: exhibition.exhibitionID,
				});
			});
			// For each trigger of an `Article` in this `Exhibition`
			// add the article to the list of articles of the trigger `Location`.
			exhibition.articles.forEach(([, article]) => {
				(article.triggers || []).forEach(({ locationID, triggerType }) => {
					if (!locationMap[locationID]) {
						logger.warn(`missing location ${locationID}`);
						return;
					}
					locationMap[locationID].exhibitions.add(exhibition.exhibitionID);
					locationMap[locationID].articles.push({
						article: article,
						exhibitionID: exhibition.exhibitionID,
						triggerType,
					});
				});
			});
		});

		// Set the landing Exhibion.
		dataSourceCFG.landingExhibition = dataSourceCFG.global.landingExhibit
			? dataSourceCFG.exhibitionMap[dataSourceCFG.global.landingExhibit.exhibitionID]
			: dataSourceCFG.exhibitions[0];
		dataSourceCFG.locationMap = locationMap;
		if (!dataSourceCFG.landingExhibition)
			throw new Error('landingExhibition is not set corretly, please check the data source settings.');

		commit(mutationTypes.setDatasource, { dataSourceURL: dsURL, dataSourceCFG });
		// set swipe through history to active or stop swiping left right through history
		commit(
			mutationTypes.swipeEnableHistory,
			dataSourceCFG.configWebClient.swipeNavigation &&
				dataSourceCFG.configWebClient.swipeNavigation.enableHistorySwipe
		);

		// set page-transition-mode, if it is not yet set by the local dev-environment
		if (!state.pageTransitionMode) {
			commit(mutationTypes.setPageTransitionMode, {
				transition:
					!state.isApp &&
					dataSourceCFG.configWebClient.swipeNavigation &&
					dataSourceCFG.configWebClient.swipeNavigation.disableSwipeInBrowser
						? 'simple'
						: 'swipe',
			});
		}
		// Init virtual root exhibition
		commit(mutationTypes.setRootExhibition);

		// Check if the device language is available,
		// if not set to fallback language as current language.
		if (!getters.possibleLanguages.includes(getters.currentLanguage)) {
			if (state.dataSourceCFG.exhibitionMap[exhibitionID]) {
				// if the exhibition does not exist
				commit(mutationTypes.setLanguage, {
					language: state.dataSourceCFG.exhibitionMap[exhibitionID].defaultLanguage,
				});
			} else {
				// Fallback Language can not estimated right now
				commit(mutationTypes.setLanguage, { language: getters.possibleLanguages[0] });
			}
		}

		// Initialize MQTT if the datasource has MQTT specified
		const { MQTTUserCredentials, mobile: mqttSettings } = state.dataSourceCFG.global.MQTTSupport;
		if (mqttSettings.active) {
			import(/* webpackChunkName: "tools/mqtt" */ './modules/mqtt').then(mqttModule => {
				registerModule('mqtt', mqttModule.default);
				dispatch(
					actionTypes.mqttConnect,
					Object.assign({}, mqttSettings, {
						username: MQTTUserCredentials.MQTTUsername ? MQTTUserCredentials.MQTTUsername : null,
						password: MQTTUserCredentials.MQTTPassword ? MQTTUserCredentials.MQTTPassword : null,
						dataSourceID: state.dataSourceCFG.dataSourceID,
					})
				).then(null, function(err) {
					logger.error('Mqtt Connection error:', err); // Error: "It broke"
				});
			});
		}
		// Initialize `nativeInterface` to to check if the webclient has to download packages.
		await initializeNativeInterface(state.isApp);
		// Show the app and ar version
		if (state.isApp) {
			nativeMethodCall({
				method: 'nativeAppGetOfflineAppsWithVersion',
				resultTransform: result => {
					if (!result) return;
					const apps = result ? JSON.parse(result) : [];
					const appsIndex = apps
						.map(({ name, version }) => ({
							name,
							version,
						}))
						.reduce((acc, { name, version }) => Object.assign(acc, { [name]: version }), {});
					const missingApps = dataSourceCFG.offlineAppWithVersionProviderInit.filter(({ name, version }) => {
						if (name === 'auto-exhibitions-list' || name === 'auto-exhibition-list') return false;
						if (name in appsIndex && appsIndex[name] !== version) {
							logger.error(
								`Package version missmatch ${name} expected version: app ${appsIndex[name]}, ds ${version}`
							);
						}
						return !(name in appsIndex);
					});
					if (missingApps.length) {
						dispatch(actionTypes.sendErrorMessage, {
							error: new Error(errorTypes.packageSyncerDownloadError),
						});
						logger.error('The following packages are missing:');
						missingApps.forEach(({ name, version }) => logger.error(`${name} version: ${version}`));
					}
				},
			});
			nativeMethodCall({
				method: 'nativeAppGetVersion',
				resultTransform: result => {
					window.console.log(`%c Native App: ${result}`, 'text-align: center; font-size: 30px;');
				},
			});
			nativeMethodCall({
				method: 'nativeInfoGetAPIVersion',
				resultTransform: result => {
					window.console.log(`%c Native API: ${result}`, 'text-align: center; font-size: 30px;');
				},
			});
			nativeMethodCall({
				method: 'nativeARGetInfo',
				resultTransform: result => {
					try {
						const resultObject = JSON.parse(result);
						window.console.log(
							`%c AR Engine: ${resultObject.EngineVersion}`,
							'text-align: center; font-size: 30px;'
						);
						window.console.log(
							`%c Unity: ${resultObject.UnityVersion}`,
							'text-align: center; font-size: 30px;'
						);
					} catch (error) {
						logger.error(`nativeARGetInfo parsing the result failed. ${error}`);
					}
				},
			});
		}

		if (dataSourceCFG.global.isProgressiveWebApp && !state.isPreview) {
			commit(mutationTypes.setImageSize, { size: 1024 });
			// Load the PWA manifest file that allows users to create a app icon on their phone
			document.head.innerHTML += `<link rel="manifest" href="${dataSourceURL}cache/manifest.webmanifest">`;
			// This must be set immediately since the first download buttons might render before the service
			// worker is loaded.
			commit(mutationTypes.packageSyncerSetIsSyncerApp, {
				mode: dataSourceCFG.configWebClient.packageSyncer.mode,
			});
			// Register service worker for progressive web app
			try {
				/* global NODE_ENV */
				if (NODE_ENV === 'production' && 'serviceWorker' in navigator) {
					window.addEventListener('load', async () => {
						logger.log('Register service-worker for the PWA');
						navigator.serviceWorker.register(`./service-worker.js?dataSourceUrl=${dataSourceURL}`);
						dispatch(actionTypes.progressiveWebAppInit, {
							serviceWorkerRegistration: await navigator.serviceWorker.ready,
						});
					});
				}
			} catch (error) {
				logger.error('Progressive web app service worker could not be installed', error);
			}
		}

		// If this is a native app check if this is running on a loaner device
		if (state.isApp) commit(mutationTypes.nativeInterfaceUp); // FIXME move me to `nativeInterface.init`; Inform all native depending modules nativeInterface is up.
		if (
			state.isApp &&
			(await nativeMethodCall({
				method: 'nativeAppGetIsLoaner',
				fallback: () => false,
			}))
		) {
			commit(mutationTypes.isLoaner);
		}

		// Initialize the package syncer if it is avaible.
		if (state.isApp || state.isVirtualPackageSyncer) {
			logger.log('init packageSyncer now');
			await dispatch(actionTypes.packageSyncerInit);
		}

		// Check if Glossary and calendarEvents have to be loaded and load it
		// In case of packageSyncer both packages has to in the initial packages so they can already be loaded
		// FIXME: wait for calendar and glossary when search is fixed
		await dispatch(actionTypes.loadGlossary);
		await dispatch(actionTypes.loadCalendar);

		// Load custom js modules (webclient plugins)
		try {
			if (plugin) {
				await dispatch(actionTypes.loadPlugin, {
					pluginName: plugin.name,
					url: plugin.url,
				});
			}
			const pluginNames = (state.dataSourceCFG.global.customJsModules || []).filter(pluginName =>
				plugin ? pluginName !== plugin.name : pluginName
			);

			await Promise.all(pluginNames.map(pluginName => dispatch(actionTypes.loadPlugin, { pluginName })));
		} catch (error) {
			logger.error('error downloadin plugins', error);
		}
	},
	async [actionTypes.loadGlossary]({ state, getters, commit }, { language } = {}) {
		if (!state.dataSourceCFG.global.showGlossary) return;
		let url;
		try {
			// get glossary package url
			const glossaryURL = state.dataSourceCFG.offlineAppWithVersionProviderInit.find(element =>
				/^glossary-/.test(element.name)
			).name;
			url = new URL(`./apps/${glossaryURL}/${language || getters.currentLanguage}.json`, state.dataSourceURL);
			const { body } = await Vue.http.get(url.toString());
			commit(mutationTypes.setGlossary, { glossary: body });
		} catch (error) {
			logger.error(`Could not initialise glossary at ${url}`);
		}
	},
	async [actionTypes.loadCalendar]({ state, getters, commit }, { language } = {}) {
		if (!state.dataSourceCFG.global.showCalendar) return;
		let url;
		try {
			// get calendar package url
			const calendarURL = state.dataSourceCFG.offlineAppWithVersionProviderInit.find(element =>
				/^calendar-/.test(element.name)
			).name;
			url = new URL(`./apps/${calendarURL}/${language || getters.currentLanguage}.json`, state.dataSourceURL);
			const { body } = await Vue.http.get(url.toString());
			const today = dayjs().startOf('day');
			commit(mutationTypes.setCalendarEvents, {
				calendarEvents: body
					// Remove events that are older than current day.
					.filter(event => !dayjs(event.date).isBefore(today))
					.map(event => Object.assign(event, { date: new Date(event.date), triggers: [] })),
			});
		} catch (error) {
			logger.error(`Could not initialise calendar at ${url}`);
		}
	},
	[actionTypes.startSubModule]({ dispatch }, { subModuleName }) {
		if (subModuleName === 'bluetooth') {
			dispatch(actionTypes.startBluetooth);
		}
	},
	[actionTypes.stopSubModule]({ dispatch }, { subModuleName }) {
		if (subModuleName === 'bluetooth') {
			dispatch(actionTypes.stopBluetooth);
		}
	},
	[actionTypes.setFontSize]({ commit, dispatch }, { size }) {
		if (Object.values(fontSizes).includes(size)) {
			commit(mutationTypes.setFontSize, { size });
		} else {
			dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.setFontSize) });
		}
	},
	[actionTypes.setColorMode]({ commit, dispatch }, { colorMode }) {
		if (Object.values(colorModes).includes(colorMode)) {
			commit(mutationTypes.setColorMode, { colorMode });
		} else {
			dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.setColorMode) });
		}
	},
	[actionTypes.openModal]({ commit, getters, state, dispatch }, { id }) {
		if (!isValidModalId(id, dispatch)) return;
		if (!isMapAvailable(id, getters, state, dispatch)) return;
		commit(mutationTypes.viewStateSetUiState, { id, uiState: uiStates.maximised });
	},
	[actionTypes.toggleModal]({ commit, state, getters, dispatch }, { id }) {
		if (!isValidModalId(id, dispatch)) return;
		if (!isMapAvailable(id, getters, state, dispatch)) return;
		if (state.viewState[id].uiState !== uiStates.hidden) {
			commit(mutationTypes.viewStateSetUiState, { id, uiState: uiStates.hidden });
		} else {
			commit(mutationTypes.viewStateSetUiState, { id, uiState: uiStates.maximised });
		}
	},
	[actionTypes.closeModal]({ commit, dispatch }, { id }) {
		if (!isValidModalId(id, dispatch)) return;
		commit(mutationTypes.viewStateSetUiState, { id, uiState: uiStates.hidden });
	},
	async [actionTypes.setLanguage]({ state, getters, commit, dispatch }, { language }) {
		if (!getters.possibleLanguages.includes(language)) {
			dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.languageNotSupported) });
			commit(mutationTypes.setLanguage, { language: state.defaultLanguage });
		} else {
			await dispatch(actionTypes.loadCalendar, { language });
			await dispatch(actionTypes.loadGlossary, { language });
			commit(mutationTypes.setLanguage, { language });
		}
		await setDayjsLocale(language, dispatch);
		dispatch(actionTypes.loadContent, {
			exhibitionID: state.currentExhibitionID,
			articleID: state.currentArticleID,
			tourID: state.tour.tourCurrentID,
		});
	},
	[actionTypes.setArticleByCode]({ getters, dispatch, state }, { code }) {
		if (typeof code !== 'number') {
			dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.pleaseEnterACode) });
			return;
		}
		const article = getters[getterTypes.articleCodeMap][code];
		if (!article) {
			dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.unkownCode) });
			return;
		}
		if (
			state.tour.active &&
			state.tour.current.exhibitIDs.find(({ articleID }) => articleID === article.articleID)
		) {
			dispatch(actionTypes.pushUrl, {
				url: `/${article.exhibitionID}/tour/${state.tour.current.id}/${article.articleID}`,
				replace: false,
			});
		} else {
			dispatch(actionTypes.pushUrl, {
				url: `/${article.exhibitionID}/${article.articleID}`,
				replace: false,
			});
		}
		// if (closeModal) commit(mutationTypes.setViewstate, { viewState: VIEW_STATE.content });
	},
	[actionTypes.setArticle]({ getters, dispatch }, { articleID, endTour }) {
		const currentExhibition = getters.currentExhibition;
		let article;
		if (articleID) {
			article = currentExhibition.articleMap[articleID];
		}
		if (!article) {
			dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.unkownArticle) });
			return;
		}
		const queryParams = Object.entries({ endTour }).reduce((acc, [paramKey, paramValue]) => {
			if (paramValue) acc.push(`${paramKey}=true`);
			return acc;
		}, []);
		dispatch(actionTypes.pushUrl, {
			// prettier-ignore
			url: `/${currentExhibition.exhibitionID}/${article.id}${queryParams.length ? `?${queryParams.join('&')}` : ''}`,
			replace: false,
		});
	},
	[actionTypes.setExhibition](
		{ getters, dispatch },
		{ exhibitionID, articleID, code, retainModal, endTour }
	) {
		// TODO: reimplement retainModal
		let article;
		const exhibition = getters.getExhibition(exhibitionID);
		if (!exhibition) {
			dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.unkownExhibition) });
			return;
		}

		if (code) {
			if (code !== 'same') {
				article = exhibition.articleMap[exhibition.codeMap[code].articleID];
			} else if (getters.currentExhibition.codeMap && exhibition.codeMap) {
				logger.log('setExhibition has codeMap');
				const codes = Object.keys(getters.currentExhibition.codeMap).filter(
					key =>
						getters.currentExhibition.codeMap[key] &&
						getters.currentExhibition.codeMap[key].articleID === getters.currentArticleID
				);
				logger.log('openn Exhibiton number', codes);
				if (codes[0]) article = exhibition.articleMap[exhibition.codeMap[codes[0]].articleID];
			}
		}
		if (articleID) {
			article = exhibition.articleMap[articleID];
		}
		if (!article) {
			logger.warn('article not found, falling back to landingArticleID');
			article = exhibition.articleMap[exhibition.landingArticleID];
			if (!article) {
				dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.unkownArticle) });
				return;
			}
		}
		logger.log('Selected Exhibition', exhibition.exhibitionID);
		logger.log('Selected Article', article.id);
		const queryParams = Object.entries({ retainModal, endTour }).reduce((acc, [paramKey, paramValue]) => {
			if (paramValue) acc.push(`${paramKey}=true`);
			return acc;
		}, []);
		// prettier-ignore
		const url = `/${exhibition.exhibitionID}/${article.id}${queryParams.length ? `?${queryParams.join('&')}` : ''}`;
		dispatch(actionTypes.pushUrl, { url, replace: false });
	},
	[actionTypes.loanerStart]({ state, dispatch }) {
		dispatch(actionTypes.setExhibition, {
			exhibitionID: state.dataSourceCFG.landingExhibition.exhibitionID,
		});
	},
	[actionTypes.openUrl]({ state, dispatch }, { url, target }) {
		if (!url) {
			logger.warn('openUrl with empty url');
			return;
		}
		// The URL is an article id or `auto-exhibtions`
		if (/^..\/[a-f0-9A-F]{24}/.test(url) || /^..\/auto-/.test(url)) {
			dispatch(actionTypes.setExhibition, {
				exhibitionID: state.currentExhibitionID,
				articleID: url.slice(3, url.length),
			});
		}

		// The URL is of type `command:` or of type `http`
		if (/^[a-z]+:[0-9]*/.test(url)) {
			if (/^command:\/\//.test(url)) {
				logger.log('Found WebClient command: ', url);
				dispatch(actionTypes.executeCommandString, url);
				return;
			}

			if (/^http[s]*:[0-9]*\/\//.test(url) || /^mailto:/.test(url)) {
				logger.log('Found External Link', url);
				if (state.isApp) nativeMethodCall({ method: 'nativeContentOpen', params: [url] });
				else window.open(url, target);
			}
		}
		// The URL start with a `/`
		else if (/^\//.test(url)) {
			// Let URLs that start with a `/` open in the current window/tab
			// so we can open urls like `/captivelogin/?redirect=market://details?id=com.shoutrlabs.shoutrboxxapp`
			window.open(url, '_self');
		}
		// The URL starts with `function`
		else if (/\[function\]/.test(url)) {
			logger.log('Link calls internal function');
		}
		// This is an unknown URL pattern, throw an error.
		else {
			logger.log('Link does not match anything:', url);
			throw new Error(errorTypes.unkownLinkPattern);
		}
	},
	[actionTypes.addToMediaObjectMap]({ commit, dispatch }, { mediaObjectArray }) {
		commit(mutationTypes.addToMediaObjectMap, { mediaObjectArray });
		dispatch(actionTypes.videoPlayQueued);
	},
};
